import React, {useState, useEffect} from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Slider,
    TextField
} from "@mui/material";

import 'dayjs/locale/it';
import {Languages} from "../types/Models/Languages";
import {LanguageAPI} from "../services/API/LanguageAPI";
import {UtilityForm} from "../utils/UtilityForm";
import {useTranslation} from "react-i18next";

interface EditLanguageProps {
    item: Languages | null;
    onSave: () => void;
    onCancel: () => void;
    open: boolean;
    onClose: () => void;
}

export const EditLanguage: React.FC<EditLanguageProps> = ( {item, onSave, onCancel, open, onClose}) =>{
    const [lang, setLang] = useState<Languages>({
        id: '',
        userId: '',
        language: '',
        isNativeLanguage: false,
        createdAt: '',
        updatedAt: '',
        isDeleted: false
    });

    const setLangDefault = () => {
        setLang({
            id: '',
            userId: '',
            language: '',
            isNativeLanguage: false,
            createdAt: '',
            updatedAt: '',
            isDeleted: false
        });
    };

    // translate
    const { t } = useTranslation();

    useEffect(() => {
        if (open) {
            if (item === null){
                setLangDefault();
            }
            else {
                setLang(item);
            }
        }
    }, [item, open]);

    const handleSaveItem = async () => {
        try {
            if (lang.id === undefined || lang.id === '') {
                await LanguageAPI.Instance<LanguageAPI>().createLanguages({
                    language: lang.language,
                    isNativeLanguage: lang.isNativeLanguage,
                    codeLevel_Listen: lang.codeLevel_Listen,
                    languageLevelId_Listen: lang.languageLevelId_Listen,
                    level_Listen: lang.level_Listen,
                    codeLevel_Read: lang.codeLevel_Read,
                    languageLevelId_Read: lang.languageLevelId_Read,
                    level_Read: lang.level_Read,
                    codeLevel_WritingSkills: lang.codeLevel_WritingSkills,
                    languageLevelId_WritingSkills: lang.languageLevelId_WritingSkills,
                    level_WritingSkills: lang.level_WritingSkills,
                    codeLevel_SpeakingSkills: lang.codeLevel_SpeakingSkills,
                    languageLevelId_SpeakingSkills: lang.languageLevelId_SpeakingSkills,
                    level_SpeakingSkills: lang.level_SpeakingSkills,
                    codeLevel_SpeakingInteraction: lang.codeLevel_SpeakingInteraction,
                    languageLevelId_SpeakingInteraction: lang.languageLevelId_SpeakingInteraction,
                    level_SpeakingInteraction: lang.level_SpeakingInteraction
                });
            }
            else {
                await LanguageAPI.Instance<LanguageAPI>().updateLanguages(lang.id, {
                    language: lang.language,
                    isNativeLanguage: lang.isNativeLanguage,
                    codeLevel_Listen: lang.codeLevel_Listen,
                    languageLevelId_Listen: lang.languageLevelId_Listen,
                    level_Listen: lang.level_Listen,
                    codeLevel_Read: lang.codeLevel_Read,
                    languageLevelId_Read: lang.languageLevelId_Read,
                    level_Read: lang.level_Read,
                    codeLevel_WritingSkills: lang.codeLevel_WritingSkills,
                    languageLevelId_WritingSkills: lang.languageLevelId_WritingSkills,
                    level_WritingSkills: lang.level_WritingSkills,
                    codeLevel_SpeakingSkills: lang.codeLevel_SpeakingSkills,
                    languageLevelId_SpeakingSkills: lang.languageLevelId_SpeakingSkills,
                    level_SpeakingSkills: lang.level_SpeakingSkills,
                    codeLevel_SpeakingInteraction: lang.codeLevel_SpeakingInteraction,
                    languageLevelId_SpeakingInteraction: lang.languageLevelId_SpeakingInteraction,
                    level_SpeakingInteraction: lang.level_SpeakingInteraction
                });
            }

            setLangDefault();
            onSave();
        } catch (error) {
            console.error('Error in insert language data:', error);
        }
    };

    const marksSlider = [
        {
            value: 0,
            label: 'A1',
        },
        {
            value: 20,
            label: 'A2',
        },
        {
            value: 40,
            label: 'B1',
        },
        {
            value: 60,
            label: 'B2',
        },
        {
            value: 80,
            label: 'C1',
        },
        {
            value: 100,
            label: 'C2',
        },
    ];

    function valuetext(value: number) {
        return ``;
    }

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{t('languageDialog.title')}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    name="language"
                    margin="dense"
                    label={t('languageDialog.language')}
                    fullWidth
                    variant="outlined"
                    value={lang.language}
                    onChange={(e) => UtilityForm.handleInputChange(e, setLang)}
                    sx={{ mb: 2 }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="isNativeLanguage"
                            checked={lang.isNativeLanguage}
                            onChange={(e) => UtilityForm.handleInputChange(e, setLang)}
                            color="primary"
                        />
                    }
                    label={t('languageDialog.nativeLanguage')}
                />

                <Divider>{t('languageDialog.understanding')}</Divider>
                <Chip label={t('languageDialog.listening')} />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Slider
                        aria-label="Livello"
                        name="level_Listen"
                        defaultValue={lang.level_Listen === null ? 0 : lang.level_Listen}
                        getAriaValueText={valuetext}
                        step={null}
                        marks={marksSlider}
                        onChange={(event, newValue, activeThumb) =>
                            UtilityForm.handleSliderChange(event, newValue, activeThumb, setLang)}
                        sx={{ width: "90%", alignItems: "center" }}
                        disabled={lang.isNativeLanguage}
                    />
                </Box>
                <Chip label={t('languageDialog.reading')} />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Slider
                        aria-label="Livello"
                        name="level_Read"
                        defaultValue={lang.level_Read === null ? 0 : lang.level_Read}
                        getAriaValueText={valuetext}
                        step={null}
                        marks={marksSlider}
                        onChange={(event, newValue, activeThumb) =>
                            UtilityForm.handleSliderChange(event, newValue, activeThumb, setLang)}
                        sx={{ width: "90%", alignItems: "center" }}
                        disabled={lang.isNativeLanguage}
                    />
                </Box>

                <Divider>{t('languageDialog.speaking')}</Divider>
                <Chip label={t('languageDialog.interaction')} />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Slider
                        aria-label="Livello"
                        name="level_SpeakingInteraction"
                        defaultValue={lang.level_SpeakingInteraction === null ? 0 : lang.level_SpeakingInteraction}
                        getAriaValueText={valuetext}
                        step={null}
                        marks={marksSlider}
                        onChange={(event, newValue, activeThumb) =>
                            UtilityForm.handleSliderChange(event, newValue, activeThumb, setLang)}
                        sx={{ width: "90%", alignItems: "center" }}
                        disabled={lang.isNativeLanguage}
                    />
                </Box>
                <Chip label={t('languageDialog.speakingSkill')} />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Slider
                        aria-label="Livello"
                        name="level_SpeakingSkills"
                        defaultValue={lang.level_SpeakingSkills === null ? 0 : lang.level_SpeakingSkills}
                        getAriaValueText={valuetext}
                        step={null}
                        marks={marksSlider}
                        onChange={(event, newValue, activeThumb) =>
                            UtilityForm.handleSliderChange(event, newValue, activeThumb, setLang)}
                        sx={{ width: "90%", alignItems: "center" }}
                        disabled={lang.isNativeLanguage}
                    />
                </Box>

                <Divider>{t('languageDialog.writingSkill')}</Divider>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Slider
                        aria-label="Livello"
                        name="level_WritingSkills"
                        defaultValue={lang.level_WritingSkills === null ? 0 : lang.level_WritingSkills}
                        getAriaValueText={valuetext}
                        step={null}
                        marks={marksSlider}
                        onChange={(event, newValue, activeThumb) =>
                            UtilityForm.handleSliderChange(event, newValue, activeThumb, setLang)}
                        sx={{ width: "90%", alignItems: "center" }}
                        disabled={lang.isNativeLanguage}
                    />
                </Box>

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('standardUse.undoBtn')}</Button>
                <Button onClick={handleSaveItem} variant="contained">
                    {t('standardUse.saveBtn')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}